/**
 * Ein Beleg im Einzelnen.
 *
 * Welche Knöpfe erscheinen, hängt vom Status ab — und zwar nach denselben
 * Regeln, die die API durchsetzt (api/src/domain/beleg-regeln.ts). Die
 * Oberfläche zeigt also nur, was auch erlaubt ist; verlassen wird sich auf
 * die Prüfung im Server, denn dort liegt die Wahrheit.
 *
 * Gebuchte Belege werden nicht gelöscht, sondern storniert — und jede
 * Änderung an einem gebuchten Beleg braucht einen Grund, der ins Journal
 * geht. Beides ist keine Höflichkeit, sondern GoBD.
 */
import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Dialog } from '../bausteine/Dialog';
import { Betragsfeld, Textbereich, Textfeld } from '../bausteine/Feld';
import { Seite } from '../bausteine/Layout';
import { Marker } from '../bausteine/Marker';
import { Fehlermeldung, Meldung } from '../bausteine/Meldung';
import { Laedt } from '../bausteine/Zustand';
import { Vorschlaege } from '../bausteine/Vorschlaege';
import { useAbruf, useVorgang } from '../lib/abruf';
import { api } from '../lib/api';
import { datum, datumZeit, groesse, prozent } from '../lib/format';
import { euro, istBetrag } from '../lib/geld';
import { useSitzung } from '../sitzung/SitzungKontext';
import type { Beleg, Kategorie } from '../lib/typen';

export function BelegDetail(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const navigiere = useNavigate();
  const { darfSchreiben } = useSitzung();

  const abruf = useAbruf<Beleg>(id ? `/api/belege/${id}` : null);
  const kategorien = useAbruf<Kategorie[]>('/api/kategorien');
  const vorgang = useVorgang();

  const [stornoOffen, setzeStornoOffen] = useState(false);
  const [stornoGrund, setzeStornoGrund] = useState('');
  const [aendernOffen, setzeAendernOffen] = useState(false);

  if (abruf.fehler) {
    return (
      <Seite titel="Beleg">
        <Fehlermeldung text={abruf.fehler} />
        <p style={{ marginTop: '1rem' }}>
          <Link to="/belege">Zurück zur Belegliste</Link>
        </p>
      </Seite>
    );
  }

  if (!abruf.daten) {
    return (
      <Seite titel="Beleg">
        <Laedt />
      </Seite>
    );
  }

  const b = abruf.daten;
  const kategorie = (kategorien.daten ?? []).find((k) => k.id === b.kategorieId);

  const buchbar =
    darfSchreiben &&
    (b.status === 'entwurf' || b.status === 'erfasst') &&
    b.zahlungsdatum !== null &&
    b.kategorieId !== null;

  const stornierbar = darfSchreiben && (b.status === 'gebucht' || b.status === 'festgeschrieben');
  const loeschbar = darfSchreiben && (b.status === 'entwurf' || b.status === 'erfasst');
  const aenderbar = darfSchreiben && b.status !== 'storniert';

  async function buchen(): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() => api.senden(`/api/belege/${b.id}/buchen`));
    if (ergebnis !== undefined) abruf.neuLaden();
  }

  async function stornieren(): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() =>
      api.senden(`/api/belege/${b.id}/stornieren`, { grund: stornoGrund.trim() }),
    );
    if (ergebnis !== undefined) {
      setzeStornoOffen(false);
      setzeStornoGrund('');
      abruf.neuLaden();
    }
  }

  async function loeschen(): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() => api.entfernen(`/api/belege/${b.id}`));
    if (ergebnis !== undefined) navigiere('/belege');
  }

  /**
   * Vorschläge in den Beleg übernehmen.
   *
   * Erst hier werden aus Vorschlägen Werte — und zwar durch einen Klick
   * eines Menschen. Automatisch zu übernehmen wäre bequemer und falsch:
   * Ein Kassenbon, auf dem „Zwischensumme" und „Summe" stehen, lässt sich
   * verlesen, und ein Betrag, der ungeprüft in die EÜR wandert, fällt
   * erst beim Jahresabschluss auf.
   */
  async function uebernehmeVorschlaege(werte: Record<string, string | number>): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() => api.aendern(`/api/belege/${b.id}`, werte));
    if (ergebnis !== undefined) abruf.neuLaden();
  }

  async function dateiHochladen(datei: File): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() =>
      api.sendeDatei<{ warnung?: string }>(`/api/belege/${b.id}/dateien`, datei),
    );
    if (ergebnis !== undefined) {
      // Eine Dublettenwarnung kommt mit Status 200 — sie ist ein Hinweis,
      // kein Fehler, und muss trotzdem sichtbar werden.
      if (ergebnis.warnung) vorgang.setzeFehler(ergebnis.warnung);
      abruf.neuLaden();
    }
  }

  return (
    <Seite
      titel={b.bezeichnung}
      untertitel={b.belegnummer ?? 'ohne Nummer'}
      aktionen={
        <>
          {aenderbar ? (
            <button type="button" className="knopf" onClick={() => setzeAendernOffen(true)}>
              Ändern
            </button>
          ) : null}
          {buchbar ? (
            <button
              type="button"
              className="knopf haupt"
              onClick={() => void buchen()}
              disabled={vorgang.laeuft}
            >
              Buchen
            </button>
          ) : null}
          {stornierbar ? (
            <button type="button" className="knopf gefahr" onClick={() => setzeStornoOffen(true)}>
              Stornieren
            </button>
          ) : null}
          {loeschbar ? (
            <button
              type="button"
              className="knopf gefahr"
              onClick={() => void loeschen()}
              disabled={vorgang.laeuft}
            >
              Verwerfen
            </button>
          ) : null}
        </>
      }
    >
      <div className="stapel" style={{ maxWidth: '1000px' }}>
        <Fehlermeldung text={vorgang.fehler} />

        {b.status === 'storniert' ? (
          <Meldung ton="gefahr" titel="Storniert.">
            Der Beleg bleibt erhalten und ist in keiner Auswertung mehr
            enthalten. Der Grund steht in der Notiz und im Journal.
          </Meldung>
        ) : null}

        {b.status === 'entwurf' ? (
          <Meldung ton="warnung" titel="Entwurf.">
            Es fehlen noch Angaben. Ein Beleg wird buchbar, sobald
            Kategorie, Betrag, Bezeichnung und Zahlungsdatum stehen.
          </Meldung>
        ) : null}

        {!b.zahlungsdatum && b.status !== 'storniert' ? (
          <Meldung ton="info" titel="Offener Posten.">
            Ohne Zahlungsdatum zählt der Beleg nicht in der EÜR — für sie
            gilt der Zufluss, nicht das Belegdatum.
          </Meldung>
        ) : null}

        <div className="raster zwei">
          <div className="karte">
            <div className="karte-kopf">
              <h2>Angaben</h2>
              <Marker status={b.status} />
            </div>
            <div className="karte-koerper">
              <dl className="paare">
                <dt>Art</dt>
                <dd>{b.richtung === 'ausgabe' ? 'Ausgabe' : 'Einnahme'}</dd>

                <dt>Belegdatum</dt>
                <dd>{datum(b.belegDatum)}</dd>

                {b.leistungsdatum ? (
                  <>
                    <dt>Leistungsdatum</dt>
                    <dd>{datum(b.leistungsdatum)}</dd>
                  </>
                ) : null}

                <dt>Zahlungsdatum</dt>
                <dd>{b.zahlungsdatum ? datum(b.zahlungsdatum) : <span className="still">offen</span>}</dd>

                <dt>Kategorie</dt>
                <dd>
                  {kategorie ? (
                    <>
                      {kategorie.bezeichnung}
                      <span className="klein still fest"> {kategorie.code}</span>
                    </>
                  ) : (
                    <span className="marker warnung">fehlt</span>
                  )}
                </dd>

                {Number.parseFloat(b.privatanteilProzent) > 0 ? (
                  <>
                    <dt>Privatanteil</dt>
                    <dd>{prozent(b.privatanteilProzent)}</dd>
                  </>
                ) : null}

                {b.notiz ? (
                  <>
                    <dt>Notiz</dt>
                    <dd style={{ whiteSpace: 'pre-wrap' }}>{b.notiz}</dd>
                  </>
                ) : null}

                <dt>Erfasst</dt>
                <dd className="klein still">
                  {datumZeit(b.erstelltAm)} · {b.quelle}
                </dd>
              </dl>
            </div>
          </div>

          <div className="karte">
            <div className="karte-kopf">
              <h2>Betrag</h2>
            </div>
            <div className="karte-koerper">
              <dl className="paare">
                <dt>Brutto</dt>
                <dd className="zahl">{euro(b.betragBrutto)}</dd>
                <dt>Netto</dt>
                <dd className="zahl">{euro(b.betragNetto)}</dd>
                <dt>Steuer ({prozent(b.steuersatz)})</dt>
                <dd className="zahl">{euro(b.steuerbetrag)}</dd>
              </dl>

              {kategorie?.abzugProzent && Number.parseFloat(kategorie.abzugProzent) < 100 ? (
                <p className="klein still" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
                  Diese Kategorie ist zu {prozent(kategorie.abzugProzent)} abziehbar — in der
                  EÜR wirkt entsprechend weniger.
                </p>
              ) : null}
            </div>
          </div>
        </div>

        <Vorschlaege
          beleg={b}
          darfSchreiben={aenderbar}
          uebernehmen={uebernehmeVorschlaege}
          laeuft={vorgang.laeuft}
          neuLaden={abruf.neuLaden}
        />

        <Dateien beleg={b} darfSchreiben={darfSchreiben} hochladen={dateiHochladen} laeuft={vorgang.laeuft} />
      </div>

      {stornoOffen ? (
        <Dialog
          titel="Beleg stornieren"
          schliessen={() => setzeStornoOffen(false)}
          fuss={
            <>
              <button type="button" className="knopf" onClick={() => setzeStornoOffen(false)}>
                Abbrechen
              </button>
              <button
                type="button"
                className="knopf gefahr"
                disabled={stornoGrund.trim().length < 3 || vorgang.laeuft}
                onClick={() => void stornieren()}
              >
                Stornieren
              </button>
            </>
          }
        >
          <div className="stapel">
            <Meldung ton="warnung">
              Der Beleg bleibt bestehen und zählt in keiner Auswertung mehr
              mit. Rückgängig geht das nicht — bei Bedarf wird neu erfasst.
            </Meldung>
            <Textbereich
              beschriftung="Grund"
              wert={stornoGrund}
              setzeWert={setzeStornoGrund}
              pflicht
              zeilen={3}
              platzhalter="Doppelt erfasst, Betrag falsch, Rechnung zurückgezogen …"
              hinweis="Geht mit Zeitpunkt und Benutzer ins Journal."
            />
          </div>
        </Dialog>
      ) : null}

      {aendernOffen ? (
        <AendernDialog
          beleg={b}
          kategorien={kategorien.daten ?? []}
          schliessen={() => setzeAendernOffen(false)}
          fertig={() => {
            setzeAendernOffen(false);
            abruf.neuLaden();
          }}
        />
      ) : null}
    </Seite>
  );
}

// =========================================================================
//  Dateien
// =========================================================================

function Dateien({
  beleg,
  darfSchreiben,
  hochladen,
  laeuft,
}: {
  beleg: Beleg;
  darfSchreiben: boolean;
  hochladen: (datei: File) => Promise<void>;
  laeuft: boolean;
}): ReactNode {
  const bilder = beleg.dateien.filter((d) => d.mimeTyp.startsWith('image/'));

  return (
    <div className="karte">
      <div className="karte-kopf">
        <h2>Belegbilder</h2>
        <span className="klein still">
          {beleg.dateien.length === 0 ? 'keine' : `${beleg.dateien.length} Datei(en)`}
        </span>
      </div>
      <div className="karte-koerper stapel">
        {beleg.dateien.length === 0 ? (
          <p className="still klein" style={{ margin: 0 }}>
            Ohne Bild ist der Beleg gültig, bei einer Prüfung aber nicht
            nachweisbar.
          </p>
        ) : (
          <div className="dateiliste">
            {beleg.dateien.map((d) => (
              <div className="dateizeile" key={d.id}>
                <span className="still">{d.seite}</span>
                <span className="name">{d.dateinameOriginal}</span>
                <span className="still klein">{groesse(d.groesseBytes)}</span>
                {/* Kein Download-Attribut: Die Datei soll sich im Browser
                    ansehen lassen, nicht im Ordner „Downloads" sammeln. */}
                <a
                  className="knopf klein"
                  href={`/api/belege/${beleg.id}/dateien/${d.id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Öffnen
                </a>
              </div>
            ))}
          </div>
        )}

        {bilder.length > 0 ? (
          <div className="raster zwei">
            {bilder.map((d) => (
              <a
                key={d.id}
                href={`/api/belege/${beleg.id}/dateien/${d.id}`}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  className="belegbild"
                  src={`/api/belege/${beleg.id}/dateien/${d.id}`}
                  alt={`Belegbild Seite ${d.seite}`}
                  loading="lazy"
                />
              </a>
            ))}
          </div>
        ) : null}

        {darfSchreiben && beleg.status !== 'storniert' ? (
          <div>
            <input
              type="file"
              accept="image/jpeg,image/png,image/heic,application/pdf"
              disabled={laeuft}
              onChange={(e) => {
                const datei = e.target.files?.[0];
                if (datei) void hochladen(datei);
                // Zurücksetzen, damit dieselbe Datei erneut gewählt werden
                // kann — nach einem Fehlversuch will man genau das.
                e.target.value = '';
              }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

// =========================================================================
//  Ändern
// =========================================================================

function AendernDialog({
  beleg,
  kategorien,
  schliessen,
  fertig,
}: {
  beleg: Beleg;
  kategorien: Kategorie[];
  schliessen: () => void;
  fertig: () => void;
}): ReactNode {
  const vorgang = useVorgang();

  const [bezeichnung, setzeBezeichnung] = useState(beleg.bezeichnung);
  const [betrag, setzeBetrag] = useState(beleg.betragBrutto.replace('.', ','));
  const [belegDatum, setzeBelegDatum] = useState(beleg.belegDatum);
  const [zahlungsdatum, setzeZahlungsdatum] = useState(beleg.zahlungsdatum ?? '');
  const [kategorieId, setzeKategorieId] = useState(beleg.kategorieId ?? '');
  const [privatanteil, setzePrivatanteil] = useState(beleg.privatanteilProzent);
  const [notiz, setzeNotiz] = useState(beleg.notiz ?? '');
  const [grund, setzeGrund] = useState('');

  // Ein gebuchter Beleg ändert sich nur mit Grund — die API weist es sonst
  // ab, und zwar zu Recht.
  const grundNoetig = beleg.status === 'gebucht' || beleg.status === 'festgeschrieben';

  const passende = kategorien.filter(
    (k) => k.art === beleg.richtung || k.art === 'privat' || k.art === 'neutral',
  );

  async function speichern(): Promise<void> {
    const aenderungen: Record<string, unknown> = {};
    if (bezeichnung.trim() !== beleg.bezeichnung) aenderungen['bezeichnung'] = bezeichnung.trim();
    if (istBetrag(betrag) && betrag !== beleg.betragBrutto.replace('.', ',')) {
      aenderungen['betragBrutto'] = betrag;
    }
    if (belegDatum !== beleg.belegDatum) aenderungen['belegDatum'] = belegDatum;
    if (zahlungsdatum !== (beleg.zahlungsdatum ?? '')) {
      aenderungen['zahlungsdatum'] = zahlungsdatum === '' ? null : zahlungsdatum;
    }
    if (kategorieId !== (beleg.kategorieId ?? '')) {
      aenderungen['kategorieId'] = kategorieId === '' ? null : kategorieId;
    }
    if (privatanteil !== beleg.privatanteilProzent) {
      aenderungen['privatanteilProzent'] = Number.parseFloat(privatanteil) || 0;
    }
    if (notiz !== (beleg.notiz ?? '')) aenderungen['notiz'] = notiz === '' ? null : notiz;

    if (Object.keys(aenderungen).length === 0) {
      vorgang.setzeFehler('Es wurde nichts geändert.');
      return;
    }
    if (grund.trim()) aenderungen['grund'] = grund.trim();

    const ergebnis = await vorgang.fuehreAus(() =>
      api.aendern(`/api/belege/${beleg.id}`, aenderungen),
    );
    if (ergebnis !== undefined) fertig();
  }

  return (
    <Dialog
      titel="Beleg ändern"
      breit
      schliessen={schliessen}
      fuss={
        <>
          <button type="button" className="knopf" onClick={schliessen}>
            Abbrechen
          </button>
          <button
            type="button"
            className="knopf haupt"
            disabled={vorgang.laeuft || (grundNoetig && grund.trim() === '')}
            onClick={() => void speichern()}
          >
            Speichern
          </button>
        </>
      }
    >
      <div className="stapel">
        <Fehlermeldung text={vorgang.fehler} />

        {grundNoetig ? (
          <Meldung ton="warnung" titel="Gebuchter Beleg.">
            Alter und neuer Wert gehen mit dem Grund ins Journal. Das ist so
            gewollt und lässt sich nicht abschalten.
          </Meldung>
        ) : null}

        <div className="formularraster">
          <Textfeld
            beschriftung="Bezeichnung"
            wert={bezeichnung}
            setzeWert={setzeBezeichnung}
            breit
            maxLaenge={300}
          />
          <Betragsfeld beschriftung="Betrag brutto" wert={betrag} setzeWert={setzeBetrag} />
          <div className="feld">
            <label htmlFor="kat-aendern">Kategorie</label>
            <select
              id="kat-aendern"
              value={kategorieId}
              onChange={(e) => setzeKategorieId(e.target.value)}
            >
              <option value="">— noch offen —</option>
              {passende.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.bezeichnung}
                </option>
              ))}
            </select>
          </div>
          <Textfeld
            beschriftung="Belegdatum"
            art="date"
            wert={belegDatum}
            setzeWert={setzeBelegDatum}
          />
          <Textfeld
            beschriftung="Zahlungsdatum"
            art="date"
            wert={zahlungsdatum}
            setzeWert={setzeZahlungsdatum}
            hinweis="Leer = noch offen"
          />
          <Textfeld
            beschriftung="Privatanteil in %"
            art="number"
            wert={privatanteil}
            setzeWert={setzePrivatanteil}
            klasse="zahl"
          />
          <Textbereich beschriftung="Notiz" wert={notiz} setzeWert={setzeNotiz} zeilen={2} breit />
          <Textbereich
            beschriftung="Grund der Änderung"
            wert={grund}
            setzeWert={setzeGrund}
            pflicht={grundNoetig}
            zeilen={2}
            breit
            hinweis={
              grundNoetig
                ? 'Pflicht bei gebuchten Belegen.'
                : 'Optional, wird aber mit protokolliert.'
            }
          />
        </div>
      </div>
    </Dialog>
  );
}
